import {
  BdArrayUtil,
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterConditionPlugin } from '../filter';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const MARKET_CONDITION_ID = 'marketId';
export const FilterConditionMarketIdPluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionMarketIdDto,
  BdMarketIdCondition
> = {
  type: 'marketIds',
  category: 'person',
  displayName: { de: 'Märkte' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionMarketIdDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionMarketIdPlugin(
      obj.includes ?? [],
      obj.excludes ?? [],
    );
  },
  fromCondition(
    jsonObj: BdMarketIdCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionMarketIdPlugin(
      jsonObj.includes ?? [],
      jsonObj.excludes ?? [],
    );
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionMarketIdPlugin([], []);
  },
};

export class FilterConditionMarketIdPlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionMarketIdDto
> {
  static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionMarketIdDto,
    BdMarketIdCondition
  > = FilterConditionMarketIdPluginType;

  constructor(
    private includes: string[],
    private excludes: string[] = [],
  ) {
    super(FilterConditionMarketIdPluginType.type);
  }

  clone(): FilterConditionMarketIdPlugin {
    return new FilterConditionMarketIdPlugin(
      [...this.includes],
      [...this.excludes],
    );
  }

  override equals(other: FilterConditionMarketIdPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionPlugin<number[]> {
    return new FilterConditionMarketIdPlugin(
      this.includes
        ? OverridePropertyHelper.overrideProperties(
            this.includes,
            'market',
            override,
            (value) => value,
          )
        : [],
      this.excludes,
    );
  }

  override getOverrideProperties(tileId: string): OverrideProperty[] {
    return this.includes.map(
      (marketId) =>
        new OverrideProperty(tileId, 'market', marketId, marketId, 'possible'),
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    return { [ShareableType.MARKET]: this.includes };
  }

  override validationToText(): string {
    if (this.includes.length === 0) {
      return 'Mindestens einen Markt auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdMarketIdCondition(this.includes, this.excludes);
  }

  toJSON(): FilterConditionMarketIdDto | null {
    return {
      type: this.type,
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.includes.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    return { title, items };
  }

  toText(): string | null {
    return this.includes.length === 1
      ? 'ein Markt'
      : `${this.includes.length} Märkte`;
  }

  toValue(): string[] {
    return this.includes;
  }
}

export class BdMarketIdCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(MARKET_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionMarketIdDto {
  type: string;
  includes: string[];
  excludes: string[];
}
